import { QueryClientProvider } from "@tanstack/react-query";
import {
  createRouter,
  type RouterHistory,
  RouterProvider,
} from "@tanstack/react-router";
import { ThemeProvider } from "next-themes";
import { PageSkeleton } from "./components/common/states";
import { queryClient } from "./lib/query-client";
import { routeTree } from "./routeTree.gen";

export interface RouterContext {
  queryClient: typeof queryClient;
}

/**
 * The router. Tests and the screenshot suite can hand it a memory history.
 *
 * `defaultRemountDeps: params` so a page remounts when a path parameter
 * changes, as a Next segment did: the open drawer or the scroll position of one
 * account does not belong to the next one.
 */
export const createAppRouter = (history?: RouterHistory) =>
  createRouter({
    routeTree,
    history,
    context: { queryClient },
    defaultRemountDeps: ({ params }) => params,
    // Avoid a flash for fast reads; slower HTTP loads show the route skeleton.
    defaultPendingComponent: PageSkeleton,
    defaultPendingMs: 400,
    defaultPendingMinMs: 300,
  });

declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}

/** The providers every screen sits inside. */
export function App({ router }: { router: ReturnType<typeof createAppRouter> }) {
  return (
    <ThemeProvider
      attribute="class"
      storageKey="aux0-theme"
      defaultTheme="system"
      enableSystem
      disableTransitionOnChange
    >
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </ThemeProvider>
  );
}
